import { useState } from "react";
import { AlertTriangle, ChevronDown, Plane, RefreshCw, Repeat } from "lucide-react";
import { BOOKING_TYPES, money, when } from "@/lib/rides";

const Row = ({ label, value, testid, tone = "" }) => (
  <div className="flex justify-between gap-3 text-sm">
    <span className="text-muted-foreground">{label}</span>
    <span className={`shrink-0 font-semibold ${tone}`} data-testid={testid}>{value}</span>
  </div>
);

function LegSummary({ quote }) {
  const legs = quote.legs || [];
  const sum = (type) => legs.filter((l) => l.type === type).reduce((s, l) => s + l.miles_actual, 0);
  const tiles = [["Buc-ee's → Pickup", sum("to_pickup")], [quote.trip_type === "round_trip" ? "Trip & return" : "Passenger trip", sum("trip")], ["Back to Buc-ee's", sum("return")]];
  return (
    <div className="grid grid-cols-3 gap-2 text-center" data-testid="quote-distance-summary">
      {tiles.map(([l, m]) => (
        <div key={l} className="rounded-xl bg-muted px-2 py-2">
          <p className="text-[10px] font-semibold uppercase leading-tight tracking-wider text-muted-foreground">{l}</p>
          <p className="mt-1 font-display text-lg font-bold">{m.toFixed(1)} mi</p>
        </div>
      ))}
    </div>
  );
}

function FareDetails({ quote }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-t pt-3">
      <button type="button" onClick={() => setOpen(!open)} data-testid="quote-details-toggle"
        className="flex w-full items-center justify-between text-xs font-semibold uppercase tracking-wider text-muted-foreground hover:text-foreground">
        Fare details <ChevronDown className={`h-4 w-4 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="mt-3 space-y-2 text-xs" data-testid="quote-details">
          {quote.legs.map((l, i) => (
            <div key={i} className="flex justify-between gap-2" data-testid="quote-leg-row">
              <span className="min-w-0 truncate text-muted-foreground">{l.from} → {l.to}</span>
              <span className="shrink-0 font-mono">{l.miles_actual} mi → {l.miles_charged} × ${l.rate.toFixed(2)} = {money(l.amount)}</span>
            </div>
          ))}
          <div className="flex justify-between border-t border-white/10 pt-2"><span className="text-muted-foreground">Mileage {money(quote.mileage_raw)} rounded down</span><span className="font-mono">{money(quote.mileage_subtotal)}</span></div>
          <div className="flex justify-between gap-2"><span className="text-muted-foreground">Farthest point: {quote.farthest.name}</span><span className="shrink-0 font-mono">{quote.farthest.miles} mi</span></div>
          {quote.waits.map((w, i) => (
            <div key={i} className="flex justify-between gap-2" data-testid="quote-wait-row"><span className="min-w-0 truncate text-muted-foreground">Wait at {w.at} · {w.minutes} min</span><span className="font-mono">{money(w.fee)}</span></div>
          ))}
          <p className="text-muted-foreground">Each leg rounded down to whole miles · only legs actually driven · {quote.vehicle_label} rates · pricing v{quote.pricing_version}</p>
        </div>
      )}
    </div>
  );
}

function WeeklyPlan({ plan, occ }) {
  return (
    <div className="space-y-2 rounded-xl bg-muted p-3 text-xs" data-testid="quote-weekly-plan">
      <p className="flex items-center gap-1 font-semibold"><Repeat className="h-3.5 w-3.5" /> {occ.length} ride{occ.length === 1 ? "" : "s"} · {plan.payment === "prepaid" ? "prepaid weekly" : "paid end of week"}{plan.applied ? ` · ${plan.pct}% off qualifying weeks` : ""}</p>
      {plan.weeks.map((w) => (
        <div key={w.week} className="flex justify-between gap-2" data-testid={`quote-week-${w.week}`}>
          <span className="text-muted-foreground">Week {w.week} · {w.rides} ride{w.rides === 1 ? "" : "s"} · {w.legs} leg{w.legs === 1 ? "" : "s"}{!w.qualifies && ` · under ${plan.min_legs_per_week} legs, no discount`}</span>
          <span className="shrink-0 font-mono">{w.qualifies ? <><s className="text-muted-foreground">{money(w.gross)}</s> {money(w.total)}</> : money(w.total)}</span>
        </div>
      ))}
      <p className="text-muted-foreground">{occ.slice(0, 4).map((o) => when(o)).join(" · ")}{occ.length > 4 ? " …" : ""}</p>
    </div>
  );
}

export function QuoteProblem({ quote, onRetry }) {
  const review = quote.status === "pricing_review";
  return (
    <div className="fade-up rounded-2xl border border-amber-400/40 bg-amber-400/10 p-4 text-amber-100" data-testid={review ? "quote-pricing-review" : "quote-route-unavailable"}>
      <p className="flex items-center gap-2 font-display text-lg font-bold tracking-wide">
        <AlertTriangle className="h-5 w-5" /> {review ? "ADMIN QUOTE REQUIRED" : "UNABLE TO CALCULATE ROUTE"}
      </p>
      <p className="mt-1 text-sm" data-testid="quote-problem-reason">
        {review ? `${quote.reason}. Submit it and a Scoopz admin will send you a price to confirm.`
          : "We couldn't get road mileage for this trip. Retry, or submit it for an admin to price."}
      </p>
      {!review && (
        <button type="button" onClick={onRetry} className="mt-3 flex items-center gap-1 text-sm font-semibold underline-offset-4 hover:underline" data-testid="quote-retry-button">
          <RefreshCw className="h-4 w-4" /> Retry
        </button>
      )}
    </div>
  );
}

export function QuoteCard({ quote, pickup, dropoff }) {
  const occ = quote.occurrences || [];
  const plan = quote.recurring?.weeks ? quote.recurring : null;
  return (
    <div className="fade-up space-y-4 rounded-2xl border bg-card p-4" data-testid="quote-card">
      <div>
        <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{BOOKING_TYPES[quote.trip_type] || "Route"}</p>
        <p className="truncate text-sm font-semibold">{pickup.name} → {dropoff.name}{quote.trip_type === "round_trip" ? " → back" : ""}</p>
      </div>
      <LegSummary quote={quote} />
      <div className="space-y-1.5 border-t pt-3">
        <Row label="Mileage (rounded down)" value={money(quote.mileage_subtotal)} testid="quote-mileage" />
        <Row label={`Base fee · farthest point ${Math.floor(quote.farthest.miles * 10) / 10} mi`} value={`+${money(quote.base_fee)}`} testid="quote-base-fee" />
        {quote.waits.length > 0 && <Row label={`Wait time (${quote.waits.map((w) => `${w.minutes}m`).join(", ")})`} value={`+${money(quote.wait_total)}`} testid="quote-wait-fee" />}
        {quote.airport.applied && <Row label={<span className="flex items-center gap-1"><Plane className="h-3.5 w-3.5" /> Airport ({quote.airport.code})</span>} value={`+${money(quote.airport.amount)}`} testid="quote-airport" />}
        <Row label="Booking fee" value={money(0)} testid="quote-booking-fee" />
      </div>
      <div className="flex items-end justify-between border-t pt-3">
        <span className="text-sm font-semibold uppercase tracking-wider">{plan ? "Per ride (before discount)" : "Ride fare"}</span>
        <span className="font-display text-4xl font-bold tracking-tight neon-text-lime" data-testid="quote-total">{money(quote.final_fare)}</span>
      </div>
      {quote.promotion && (
        <p className="rounded-xl border border-primary/40 bg-primary/10 px-3 py-2 text-xs" data-testid="quote-promotion">
          <b className="text-primary">PROMO FARE {money(quote.promotion.fare)}</b> · {quote.promotion.location_name} ({quote.promotion.window}) replaces the standard <span className="line-through">{money(quote.promotion.standard_fare)}</span>
        </p>
      )}
      {!!quote.promo_rides && <p className="rounded-xl border border-primary/40 bg-primary/10 px-3 py-2 text-xs" data-testid="quote-promo-rides">{quote.promo_rides} of these rides get a promotional flat fare (no recurring discount on those). Series total with promos: <b>{money((quote.occurrence_fares || []).reduce((a, b) => a + b, 0))}</b></p>}
      {plan && (
        <>
          <WeeklyPlan plan={plan} occ={occ} />
          <Row label={`Series total${plan.applied ? ` (save ${money(plan.series_gross - plan.series_total)})` : ""}`} value={money(plan.series_total)} testid="quote-series-total" tone="neon-text-lime" />
        </>
      )}
      <FareDetails quote={quote} />
    </div>
  );
}
